// Event detail modal — shows title, range, location, notes. Edit
// switches into the new-event sheet pre-populated. Remove confirms via
// the themed confirm() before deletion.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { confirm } from "../../components/confirm";
import { api, type Calendar, type CalendarEvent } from "../../lib/rpc";
import { CAL_DAYS_LONG, colorFor, fmtTime, isoToHour } from "../../lib/calendar-utils";
import { InviteesPanel } from "./invitees-panel";

interface EventDetailProps {
  event: CalendarEvent;
  calendars: Calendar[];
  meId: string | null;
  notify: (msg: string) => void;
  onClose: () => void;
  onEdit: () => void;
  onRemove: () => Promise<void> | void;
  onChanged: () => void;
}

export function EventDetail({ event, calendars, meId, notify, onClose, onEdit, onRemove, onChanged }: EventDetailProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Pull this event's reminder offsets so the user can see what's
  // scheduled. Edit goes through the new-event sheet (which has the
  // full chip picker) — keeping the detail view read-only here.
  const [reminders, setReminders] = useState<number[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    api.calendarListReminders(event.masterId || event.id)
      .then((r) => { if (!cancelled) setReminders(r); })
      .catch(() => { if (!cancelled) setReminders([]); });
    return () => { cancelled = true; };
  }, [event.id, event.masterId]);

  const start = new Date(event.dtstart);
  const end = new Date(event.dtend);
  const sameDayRange = start.toDateString() === end.toDateString();
  const dayStr = sameDayRange
    ? `${CAL_DAYS_LONG[start.getDay()]}, ${start.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : `${start.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} – ${end.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`;
  const timeStr = event.allDay
    ? "all day"
    : `${fmtTime(isoToHour(event.dtstart))} – ${fmtTime(isoToHour(event.dtend))}`;
  const calendar = event.calendarId ? calendars.find((c) => c.id === event.calendarId) : null;

  const handleRemove = async () => {
    const ok = await confirm({
      title: `Remove "${event.title}"?`,
      message: "This event will be deleted from your calendar. This can't be undone.",
      confirmLabel: "Remove",
      danger: true,
    });
    if (!ok) return;
    await onRemove();
  };

  return (
    <div className="cal-modal-bg" onClick={onClose}>
      <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span
            style={{
              width: 6, alignSelf: "stretch", borderRadius: 4,
              background: `var(--${colorFor(event, calendars)})`,
            }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 className="cal-modal-title">{event.title}</h3>
            <div className="cal-modal-meta">
              {dayStr} · {timeStr}
              {calendar && <span className="cal-modal-cal"> · {calendar.name}</span>}
            </div>
            {event.location && (
              <div className="cal-modal-loc">
                <span className="eyebrow" style={{ marginRight: 6 }}>where</span>{event.location}
              </div>
            )}
            {event.description && (
              <div className="cal-modal-notes">{event.description}</div>
            )}
            {reminders && reminders.length > 0 && (
              <div className="cal-modal-reminders">
                <span className="eyebrow" style={{ marginRight: 6 }}>remind</span>
                {reminders
                  .slice()
                  .sort((a, b) => a - b)
                  .map(fmtReminderOffset)
                  .join(" · ")}
              </div>
            )}
            {event.rrule && (
              <div className="cal-modal-reminders">
                <span className="eyebrow" style={{ marginRight: 6 }}>repeats</span>
                {fmtRrule(event.rrule)}
              </div>
            )}
          </div>
          <button className="icon-btn" onClick={onClose} type="button">
            <Icon name="x" size={14} />
          </button>
        </div>
        <InviteesPanel
          eventId={event.masterId || event.id}
          myInviteStatus={event.myInviteStatus}
          meId={meId}
          notify={notify}
          onChanged={onChanged}
        />

        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <button className="btn" onClick={onEdit} type="button">
            <Icon name="edit" size={14} /> Edit
          </button>
          <button className="btn" onClick={handleRemove} type="button">
            <Icon name="trash" size={14} /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// Friendly "10 min", "1 hour", "2 days" labels for the reminder
// summary in event detail. 0 collapses to "at start"; everything
// else rounds to the largest sensible unit.
function fmtReminderOffset(mins: number): string {
  if (mins === 0) return "at start";
  if (mins < 60) return `${mins} min`;
  if (mins < 1440) {
    const h = mins / 60;
    return `${h % 1 === 0 ? h.toFixed(0) : h.toFixed(1)} hour${h === 1 ? "" : "s"}`;
  }
  const d = mins / 1440;
  return `${d % 1 === 0 ? d.toFixed(0) : d.toFixed(1)} day${d === 1 ? "" : "s"}`;
}

// Translate the canonical preset RRULEs back into human phrasing. For
// anything non-canonical (custom rules with BYSETPOS, multiple BYDAYs,
// COUNT/UNTIL clauses, ...) we just show the raw rrule string — fully
// expressive but mildly cryptic. Picker can round-trip these via
// presetForRrule → "custom" → raw input.
function fmtRrule(rrule: string): string {
  if (rrule === "FREQ=DAILY") return "Daily";
  if (rrule === "FREQ=YEARLY") return "Yearly";
  if (/^FREQ=WEEKLY;BYDAY=[A-Z]{2}$/.test(rrule)) return "Weekly";
  if (/^FREQ=MONTHLY;BYMONTHDAY=\d{1,2}$/.test(rrule)) return "Monthly";
  return rrule;
}
